import * as THREE from 'three';
import { getSprites } from './textures';
import { START_X, latticeOffsets, nullGeodesic, polylineLength, resampleByArc, rotateAboutX, sampleXYZ, smoothstep } from './geodesic';

const THREAD_POINTS = 70;
const PULSES_PER_THREAD = 3;
/** how fast a pulse of light crosses the picture, world units per second */
const PULSE_SPEED = 3.4;

const GOLD: [number, number, number] = [1.0, 0.72, 0.30];
const BLUE: [number, number, number] = [0.42, 0.66, 1.0];
const PULSE_NEAR: [number, number, number] = [1.0, 0.86, 0.55];
const PULSE_FAR: [number, number, number] = [0.62, 0.84, 1.0];

/**
 * The light lattice: a grid of rays that all start at x = -9 moving +x, each integrated through the Sun's curved spacetime
 * and rotated about the x axis into 3D. Drawn as thin threads, gold near the Sun and blue far from it,
 * with three bright pulses riding every thread so the light visibly flows.
 */
export class LightLattice {
  readonly group = new THREE.Group();
  private threads: Float32Array[] = [];
  private lengths: number[] = [];
  private phase: number[] = [];
  private flowPos: Float32Array;
  private flowCol: Float32Array;
  private flowGeo = new THREE.BufferGeometry();
  private tmp = new Float32Array(3);

  constructor() {
    const pos: number[] = [], col: number[] = [];
    for (const [gy, gz] of latticeOffsets()) {
      const b = Math.hypot(gy, gz);
      const g = nullGeodesic(b, 'einstein', { x0: START_X });
      const pts = rotateAboutX(resampleByArc(g.points, THREAD_POINTS), Math.atan2(gz, gy));
      this.threads.push(pts);
      this.lengths.push(polylineLength(g.points));
      for (let i = 0; i < THREAD_POINTS - 1; i++) {
        for (let k = 0; k < 2; k++) {
          const j = i + k, r = Math.hypot(pts[3 * j], pts[3 * j + 1], pts[3 * j + 2]);
          const m = smoothstep(0, 1, (r - 1) / 6), s = j / (THREAD_POINTS - 1);
          // fade in from the left edge and out where the thread ends, so no thread starts or stops with a hard tip
          const fade = smoothstep(0, 0.12, s) * smoothstep(1, 0.88, s);
          pos.push(pts[3 * j], pts[3 * j + 1], pts[3 * j + 2]);
          col.push((GOLD[0] + (BLUE[0] - GOLD[0]) * m) * fade, (GOLD[1] + (BLUE[1] - GOLD[1]) * m) * fade, (GOLD[2] + (BLUE[2] - GOLD[2]) * m) * fade);
        }
      }
    }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    lg.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
    const lines = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.group.add(lines);

    const n = this.threads.length * PULSES_PER_THREAD;
    this.flowPos = new Float32Array(n * 3);
    this.flowCol = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) this.phase.push(((i * 0.6180339887) % 1));
    this.flowGeo.setAttribute('position', new THREE.BufferAttribute(this.flowPos, 3));
    this.flowGeo.setAttribute('color', new THREE.BufferAttribute(this.flowCol, 3));
    const flow = new THREE.Points(this.flowGeo, new THREE.PointsMaterial({
      map: getSprites().hard, size: 0.14, vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true
    }));
    flow.frustumCulled = false;
    this.group.add(flow);
    this.update(0);
  }

  get threadCount() { return this.threads.length; }

  /** Move every pulse along its thread. Only typed-array writes; no geometry is rebuilt. */
  update(time: number) {
    const out = this.tmp;
    for (let i = 0; i < this.threads.length; i++) {
      const pts = this.threads[i], step = (PULSE_SPEED * time) / Math.max(this.lengths[i], 1e-3);
      for (let k = 0; k < PULSES_PER_THREAD; k++) {
        const id = i * PULSES_PER_THREAD + k, o = id * 3;
        const s = (((this.phase[id] + k / PULSES_PER_THREAD + step) % 1) + 1) % 1;
        sampleXYZ(pts, THREAD_POINTS, s * (THREAD_POINTS - 1), out, 0);
        this.flowPos[o] = out[0]; this.flowPos[o + 1] = out[1]; this.flowPos[o + 2] = out[2];
        const m = smoothstep(1, 7, Math.hypot(out[0], out[1], out[2]));
        const gain = 1.3 * smoothstep(0, 0.06, s) * smoothstep(1, 0.94, s);
        this.flowCol[o] = (PULSE_NEAR[0] + (PULSE_FAR[0] - PULSE_NEAR[0]) * m) * gain;
        this.flowCol[o + 1] = (PULSE_NEAR[1] + (PULSE_FAR[1] - PULSE_NEAR[1]) * m) * gain;
        this.flowCol[o + 2] = (PULSE_NEAR[2] + (PULSE_FAR[2] - PULSE_NEAR[2]) * m) * gain;
      }
    }
    this.flowGeo.attributes.position.needsUpdate = true;
    this.flowGeo.attributes.color.needsUpdate = true;
  }
}
